// AdminHeader — wordmark, workspace switcher, search, live status, identity.
// The switcher renders only the workspaces the current user may enter
// (WORKSPACE_ACCESS); a single-workspace user sees just the label, no dropdown.
import React, { useState, useEffect } from 'react';
import { Bell, Settings, Search, ChevronsUpDown, Check, LifeBuoy, Clapperboard } from 'lucide-react';
import { useNavigate } from 'react-router';
import { supabase } from '@/lib/supabase';
import { markIntentionalSignOut } from '@/lib/session-signout';
import { PRODUCT_NAME } from '@/lib/brand';
import { PolesteadLockup } from '@/lib/PolesteadMark';
import { Icon } from '@/components/shared/Icon';
import { usePersona } from '@/context/PersonaProvider';
import { PERSONA, mayAuthorWalkthroughs, type PersonaKey } from '@/lib/persona';
import { CommandPalette } from '@/components/shared/CommandPalette';
import { NotificationsDrawer } from '@/components/shared/NotificationsDrawer';
import { ThemeToggle } from '@/components/shared/ThemeToggle';
import { LiveDot } from '@/components/shared/LiveDot';
import { ReportButton } from '@/components/reporter';
import { openStudio, useStudioSession } from '@/components/reporter/StudioPanel';

const iconBtn: React.CSSProperties = {
  width: 32, height: 32, borderRadius: 8, display: 'flex', alignItems: 'center',
  justifyContent: 'center', color: 'var(--text-secondary)', cursor: 'pointer', background: 'transparent', border: 'none',
};

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).slice(0, 2);
  const ini = parts.map((w) => w[0]).join('').toUpperCase();
  return ini || PRODUCT_NAME.slice(0, 2).toUpperCase();
}

export function AdminHeader({ persona }: { persona: PersonaKey }) {
  const navigate = useNavigate();
  const { user, workspaces, switchPersona } = usePersona();
  const [open, setOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [bellOpen, setBellOpen] = useState(false);
  const p = PERSONA[persona];
  const multi = workspaces.length > 1;
  // A20 W8 — is a walkthrough session running, and may this user start one?
  // `mayAuthorWalkthroughs` draws a button and nothing more; assertStudioAuthor
  // on the verified session is the fence (see lib/persona.ts).
  const studioActive = useStudioSession();
  const mayAuthor = mayAuthorWalkthroughs(user?.role, user?.isAdmin);

  // Cmd/Ctrl-K opens the command palette.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const handleLogout = async () => {
    // A deliberate sign-out: tell the idle detector's SIGNED_OUT listener, which would
    // otherwise treat it as a session that ended underneath the tab ("Please sign in again").
    markIntentionalSignOut();
    await supabase.auth.signOut();
    navigate('/login');
  };

  return (
    <>
    <header
      className="header-bar sticky top-0 z-50"
      style={{ height: 'var(--header-h)', background: 'var(--surface)', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', padding: '0 20px', gap: 14, position: 'relative', zIndex: 40 }}
    >
      <PolesteadLockup markSize={16} fontSize={16} />
      <span style={{ width: 1, height: 22, background: 'var(--border)' }} />
      <span style={{ fontSize: 13.5, color: 'var(--text-secondary)' }}>License Server</span>

      {/* Workspace switcher */}
      <div style={{ position: 'relative', marginLeft: 6 }}>
        <button
          onClick={() => multi && setOpen((o) => !o)}
          style={{
            display: 'flex', alignItems: 'center', gap: 8, padding: '5px 10px 5px 8px', borderRadius: 8,
            border: '1px solid var(--border)', background: p.tint, cursor: multi ? 'pointer' : 'default', fontFamily: 'inherit',
          }}
          title={multi ? 'Switch workspace' : p.label}
        >
          <span style={{ width: 22, height: 22, borderRadius: 6, background: p.accent, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icon name={p.icon} size={13} color="#fff" />
          </span>
          <span style={{ textAlign: 'left' }}>
            <span style={{ display: 'block', fontSize: 12.5, fontWeight: 700, color: p.accent, lineHeight: 1.1 }}>{p.label}</span>
            <span style={{ display: 'block', fontSize: 10, color: 'var(--text-muted)', lineHeight: 1.1 }}>Workspace</span>
          </span>
          {multi && <ChevronsUpDown size={13} color="var(--text-muted)" />}
        </button>
        {open && multi && (
          <>
            <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 1 }} />
            <div style={{ position: 'absolute', top: 42, left: 0, width: 240, background: 'var(--surface-raised)', border: '1px solid var(--border)', borderRadius: 12, boxShadow: 'var(--shadow-lg)', padding: 6, zIndex: 2 }}>
              <div style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--text-muted)', padding: '8px 10px 6px' }}>Switch workspace</div>
              {workspaces.map((key) => {
                const op = PERSONA[key];
                const on = key === persona;
                return (
                  <button
                    key={key}
                    onClick={() => { switchPersona(key); setOpen(false); }}
                    style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '9px 10px', borderRadius: 8, border: 'none', background: on ? op.tint : 'transparent', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left' }}
                  >
                    <span style={{ width: 28, height: 28, borderRadius: 7, background: op.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Icon name={op.icon} size={15} color="#fff" />
                    </span>
                    <span style={{ flex: 1 }}>
                      <span style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{op.label}</span>
                      <span style={{ display: 'block', fontSize: 11.5, color: 'var(--text-muted)' }}>{op.sub}</span>
                    </span>
                    {on && <Check size={15} color={op.accent} />}
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>

      <div style={{ flex: 1 }} />
      <div onClick={() => setPaletteOpen(true)} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 12px', background: 'var(--bg-muted)', border: '1px solid var(--border)', borderRadius: 8, width: 220, color: 'var(--text-muted)', fontSize: 13, cursor: 'pointer' }}>
        <Search size={13} /> <span style={{ flex: 1 }}>Search…</span>
        <kbd style={{ fontSize: 10.5, border: '1px solid var(--border)', borderRadius: 4, padding: '1px 5px', background: 'var(--surface)' }}>⌘K</kbd>
      </div>
      <LiveDot status="live" label="Live" style={{ fontSize: 12.5 }} />
      <ThemeToggle />
      {/* A20 W2b — report a problem. This header has no help icon, so the
          button takes the slot the shared Topper gives it: immediately before
          notifications, inside the utility cluster.

          A20 C3 — THE PROVIDER NO LONGER LIVES HERE. It was mounted in this
          header, wrapping this one button, which meant it was a SIBLING of the
          routed content rather than an ancestor of it: React context only flows
          down, so no error boundary around a page could ever reach it, and a
          page crash could not offer a report that carried the buffer. App.tsx's
          PortalShell now mounts it one node above both this header and the
          content (see ReporterHost there), and this is just the consumer.

          The swap is unchanged and still absolute — the bug reporter and the
          walkthrough studio are never mounted together, because rrweb 2.1.1's
          module-scope `wrappedEmit` means a second record() hijacks the first
          rather than adding to it. It simply happens in ReporterHost now. The
          guard below preserves the visible consequence exactly: while a
          walkthrough is recording there is no "Report a problem" in the header,
          because the provider above is the STUDIO one and its dialog is the
          studio panel, not the report dialog. */}
      {!studioActive && <ReportButton />}
      {/* Start a walkthrough from anywhere. The durable home for the feature is
          the NAV entry + /support/studio (the library, and the same button);
          this is the in-chrome shortcut for an author who is already on the
          screen they want to record and would lose it by navigating away. */}
      {mayAuthor && !studioActive && (
        <button style={iconBtn} onClick={openStudio} title="Record a walkthrough">
          <Clapperboard size={17} />
        </button>
      )}
      {/* Triage lives at /support/tickets (A20 W7a). Shown to admins only: the
          reads behind it are platform-operator reads and a non-admin would just
          get a 403. The durable home is the Support NAV section in
          lib/persona.ts, which now exists; this stays as the one-click route
          from any screen, the same role the studio button beside it plays. */}
      {user?.isAdmin && (
        <button style={iconBtn} onClick={() => navigate('/support/tickets')} title="Support triage">
          <LifeBuoy size={17} />
        </button>
      )}
      <button style={iconBtn} onClick={() => setBellOpen(true)} title="Notifications">
        <Bell size={17} />
      </button>
      <button style={iconBtn} onClick={() => navigate('/settings')} title="Settings">
        <Settings size={16} />
      </button>
      <div
        title={`${user?.name || 'User'} (${user?.role || ''}) — click to sign out`}
        onClick={handleLogout}
        style={{ width: 30, height: 30, borderRadius: '50%', background: p.accent, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
      >
        {initialsOf(user?.name || PRODUCT_NAME)}
      </div>
    </header>
    <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
    <NotificationsDrawer open={bellOpen} onClose={() => setBellOpen(false)} />
    </>
  );
}
